import { Search } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Customer } from "../../../data/models/customers/CustomerModel";

interface CustomersTableProps {
  customers: Customer[];
  deleteCustomer: (id: string) => Promise<void>;
  onEdit: (customer: Customer) => void;
}

export default function CustomersTable({
  customers,
  deleteCustomer,
  onEdit,
}: CustomersTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [search, setSearch] = useState("");
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const filteredProducts = useMemo(() => {
    return customers.filter((c) => {
      const text = search.toLowerCase();
      return (
        c.fullName.toLowerCase().includes(text) ||
        c.email.toLowerCase().includes(text)
      );
    });
  }, [customers, search]);

  const handleDelete = async (id: string) => {
    setLoadingId(id);
    await deleteCustomer(id);
    setLoadingId(null);
  };

  const columns: TableColumn<Customer>[] = [
    {
      name: "Nombres",
      selector: (row) => row.fullName,
      sortable: true,
    },
    {
      name: "Tipo de documento",
      selector: (row) => row.documentType,
      sortable: true,
    },
    {
      name: "Documento",
      selector: (row) => row.documentNumber,
      sortable: true,
    },
    {
      name: "Teléfono",
      selector: (row) => row.phone,
      right: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      right: true,
      sortable: true,
    },
    {
      name: "Acciones",
      cell: (row) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => onEdit(row)}
            disabled={loadingId !== null}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.id!)}
            disabled={loadingId !== null}
          >
            {loadingId === row.id ? (
              <CircularProgress size={16} />
            ) : (
              <DeleteIcon fontSize="small" />
            )}
          </IconButton>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        overflowX: "auto",
        padding: 2,
        marginY: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} mb={1}>
        Clientes
      </Typography>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <TextField
          size="small"
          label="Buscar cliente o correo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <DataTable
        fixedHeader
        fixedHeaderScrollHeight="400px"
        columns={columns}
        data={filteredProducts}
        pagination
        highlightOnHover
        striped
        noDataComponent="No hay clientes para mostrar"
        paginationComponentOptions={{
          rowsPerPageText: "Filas por página",
          rangeSeparatorText: "de",
          selectAllRowsItem: false,
        }}
        customStyles={{
          noData: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          },
          table: {
            style: {
              width: isMobile ? "80vw" : "100%",
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
            },
          },
          headRow: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          },
          headCells: {
            style: {
              fontWeight: 600,
              fontSize: "14px",
              color: theme.palette.text.primary,
            },
          },
          rows: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              minHeight: "48px",
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            stripedStyle: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              minHeight: "48px",
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            highlightOnHoverStyle: {
              backgroundColor: theme.palette.divider,
              border: "transparent",
              color: theme.palette.text.primary,
              transitionDuration: "0.2s",
              transitionProperty: "background-color",
              cursor: "pointer",
            },
          },
          pagination: {
            style: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderTop: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      />
    </Card>
  );
}
